import { useCallback, useEffect, useMemo, useState } from 'react';
import * as bd from 'react-basic-design';
import {
    BoardDTO,
    useSearchBoardApi,
    useSaveBoardApi,
    useDeleteBoardApi,
} from '../../../api/board-api';
import SvgAdd from '../../../assets/icons/Add';
import { N } from '../../../components/basic/n';
import { notify } from '../../../components/basic/notify';
import { T, translate } from '../../../components/basic/text';
import { FormikCellToggle } from '../../../components/formik';
import { DateCell, ToggleCell } from '../../../components/table/table-cells';
import { PageNavbar } from '../../../components/ui/page-navbar';
import { TProvider } from '../../../i18n';
import { IBasicTableColumn } from '../../../interfaces/interfaces';
import { SaveBoardModal } from './save-board-modal';
import { LoadingButton } from '../../../components/loading-button';
import { InitFailed } from '../../../components/basic/init-failed';
import { Loading } from '../../../components/basic/loading';
import { ModifiedRowInfo } from '../../../components/table/modified-row-info';
import { CreatedRowInfo } from '../../../components/table/created-row-info';

export function BoardApp() {
    const [showModal, setShowModal] = useState(false);
    const [selectedBoard, setSelectedBoard] = useState<BoardDTO | undefined>();
    const boardInit = useSearchBoardApi();
    const saveBoard = useSaveBoardApi();
    const deleteBoard = useDeleteBoardApi();

    const { result, error, busy, initialized, initializing } = boardInit;

    const columns: IBasicTableColumn<BoardDTO>[] = useMemo(
        () => [
            {
                Header: <T>id</T>,
                name: 'id',
                width: 80,
                Cell: ({ row }) => <N>{row.id}</N>,
            },
            {
                Header: <T>title</T>,
                name: 'title',
                width: 80,
            },
            {
                Header: <T>height</T>,
                name: 'height',
                width: 80,
                Cell: ({ row }) => <N>{row.height}</N>,
            },
            {
                Header: <T>width</T>,
                name: 'width',
                width: 80,
                Cell: ({ row }) => <N>{row.width}</N>,
            },
            {
                Header: <T>latitude</T>,
                name: 'latitude',
                width: 80,
                Cell: ({ row }) => <N>{row.latitude}</N>,
            },
            {
                Header: <T>longitude</T>,
                name: 'longitude',
                width: 80,
                Cell: ({ row }) => <N>{row.longitude}</N>,
            },
            {
                Header: <T>address</T>,
                name: 'address',
                width: 80,
            },

            {
                Header: <T>created-by</T>,
                name: 'createdBy',
                width: 100,
                Cell: CreatedRowInfo,
            },
            {
                Header: <T>modified-by</T>,
                name: 'modifiedBy',
                width: 100,
                Cell: ModifiedRowInfo,
            },
        ],
        []
    );

    useEffect(() => {
        if (initialized || initializing || error) return;
        boardInit.post(null, {});
    }, [initialized, initializing, error, boardInit]);

    const handleDelete = (board: BoardDTO) => {
        bd.msgbox(
            translate('delete-board'),
            translate('confirm-delete-board'),
            [
                {
                    title: translate('delete'),
                    variant: 'contained',
                    color: 'secondary',
                    action: hide => {
                        deleteBoard.remove(
                            board,
                            () => {
                                boardInit.updateResult({
                                    ...result,
                                    boards: result.boards.filter(b => b.id !== board.id),
                                });
                                notify.dark(translate('selected-board-deleted-successfully'));
                                hide();
                                setShowModal(false);
                                setSelectedBoard(undefined);
                            },
                            notify.error
                        );
                    },
                },
                {
                    title: translate('cancel'),
                    variant: 'outline',
                    color: 'secondary',
                },
            ],
            { className: 'modal-n', backdropClassName: 'modal-n' }
        );
    };

    const handleSave = (board: BoardDTO) => {
        saveBoard.post(
            null,
            board,
            res => {
                if (selectedBoard) {
                    boardInit.updateResult({
                        ...result,
                        boards: result.boards.map(b => (b.id === board.id ? res.board : b)),
                    });
                } else {
                    boardInit.updateResult({
                        ...result,
                        boards: [res.board, ...result.boards],
                    });
                }

                setShowModal(false);
                setSelectedBoard(undefined);
                notify.success(
                    selectedBoard
                        ? translate('selected-board-updated-successfully')
                        : translate('new-board-created-successfully')
                );
            },
            notify.error
        );
    };

    if (error) return <InitFailed ex={error} onRetry={() => boardInit.post(null, {})} />;
    if (!initialized || initializing) return <Loading />;

    return (
        <TProvider>
            <div className="container">
                <PageNavbar
                    onGoBack="/home"
                    title="board"
                    controls={
                        <LoadingButton
                            variant="contained"
                            size="sm"
                            label={<T>new-board</T>}
                            icon={<SvgAdd />}
                            onClick={() => {
                                setShowModal(true);
                                setSelectedBoard(undefined);
                            }}
                            loading={busy}
                        />
                    }
                />
                <bd.Paper className="h-100">
                    <bd.BasicTable
                        columns={columns}
                        data={result.boards}
                        onRowClick={({ row }) => {
                            setSelectedBoard(row);
                            setShowModal(true);
                        }}
                        hover
                    />

                    <SaveBoardModal
                        show={showModal}
                        onHide={() => {
                            setShowModal(false);
                            setSelectedBoard(undefined);
                        }}
                        onSave={handleSave}
                        onDelete={handleDelete}
                        selectedBoard={selectedBoard}
                        allowEdit={result.allowEdit}
                    />
                </bd.Paper>
            </div>
        </TProvider>
    );
}
